import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AccountId, UserId } from 'api/models';
import { ForReviewItemStatus, ForReviewListStatus, isForReviewUnread, nitroReviewAPI } from 'api/nitro-review';
import { Action, EventType, ObjectType, pushes, UserChannel } from 'api/pushes';
import { useCallback, useEffect } from 'react';
import { useAccountDataQuery } from 'queries/account';
export const FOR_REVIEW_QUERY_KEYS = {
    all: [
        'for-review'
    ],
    list: (status = ForReviewListStatus.All)=>[
            ...FOR_REVIEW_QUERY_KEYS.all,
            'list',
            status
        ]
};
const markNotificationStatus = (groups, notificationId, status)=>groups === null || groups === void 0 ? void 0 : groups.map((group)=>({
            ...group,
            notifications: group.notifications.map((notification)=>notification.id === notificationId ? {
                    ...notification,
                    status
                } : notification)
        }));
export const useForReviewNotificationsQuery = (status = ForReviewListStatus.All)=>useQuery({
        queryKey: FOR_REVIEW_QUERY_KEYS.list(status),
        queryFn: ()=>nitroReviewAPI.getForReviewNotifications({
                status
            })
    });
/**
 * Clears a for-review notification (replaces last-seen). Optimistically marks
 * status Cleared so the unread dot disappears before the PATCH returns.
 */ export const useClearForReviewNotification = ()=>{
    const queryClient = useQueryClient();
    return useCallback((notificationId)=>{
        queryClient.setQueriesData({
            queryKey: FOR_REVIEW_QUERY_KEYS.all
        }, (current)=>{
            var _markNotificationStatus;
            return (_markNotificationStatus = markNotificationStatus(current, notificationId, ForReviewItemStatus.Cleared)) !== null && _markNotificationStatus !== void 0 ? _markNotificationStatus : current;
        });
        void nitroReviewAPI.clearForReviewItem(notificationId).then(async ()=>{
            await queryClient.invalidateQueries({
                queryKey: FOR_REVIEW_QUERY_KEYS.all
            });
            // Refetch can briefly return New (eventual consistency). Re-assert
            // Cleared so ClearOnOpen's one-shot guard doesn't leave the item unread.
            queryClient.setQueriesData({
                queryKey: FOR_REVIEW_QUERY_KEYS.all
            }, (current)=>{
                var _markNotificationStatus;
                return (_markNotificationStatus = markNotificationStatus(current, notificationId, ForReviewItemStatus.Cleared)) !== null && _markNotificationStatus !== void 0 ? _markNotificationStatus : current;
            });
        }).catch(async ()=>{
            // PATCH failed — drop the optimistic Cleared and refetch server state.
            await queryClient.invalidateQueries({
                queryKey: FOR_REVIEW_QUERY_KEYS.all
            });
        });
    }, [
        queryClient
    ]);
};
/**
 * Restores a for-review notification to unread. Optimistically marks status New
 * so the unread dot returns before the PATCH returns.
 */ export const useUnclearForReviewNotification = ()=>{
    const queryClient = useQueryClient();
    return useCallback((notificationId)=>{
        queryClient.setQueriesData({
            queryKey: FOR_REVIEW_QUERY_KEYS.all
        }, (current)=>{
            var _markNotificationStatus;
            return (_markNotificationStatus = markNotificationStatus(current, notificationId, ForReviewItemStatus.New)) !== null && _markNotificationStatus !== void 0 ? _markNotificationStatus : current;
        });
        void nitroReviewAPI.unclearForReviewItem(notificationId).then(async ()=>{
            await queryClient.invalidateQueries({
                queryKey: FOR_REVIEW_QUERY_KEYS.all
            });
            queryClient.setQueriesData({
                queryKey: FOR_REVIEW_QUERY_KEYS.all
            }, (current)=>{
                var _markNotificationStatus;
                return (_markNotificationStatus = markNotificationStatus(current, notificationId, ForReviewItemStatus.New)) !== null && _markNotificationStatus !== void 0 ? _markNotificationStatus : current;
            });
        }).catch(async ()=>{
            // PATCH failed — drop the optimistic New and refetch server state.
            await queryClient.invalidateQueries({
                queryKey: FOR_REVIEW_QUERY_KEYS.all
            });
        });
    }, [
        queryClient
    ]);
};
export const useForReviewPending = ()=>{
    var _ref;
    var _accountData_account, _accountData_userProfile_user, _accountData_userProfile;
    const queryClient = useQueryClient();
    const { data: accountData } = useAccountDataQuery();
    const accountId = accountData === null || accountData === void 0 ? void 0 : (_accountData_account = accountData.account) === null || _accountData_account === void 0 ? void 0 : _accountData_account.accountId;
    const userId = accountData === null || accountData === void 0 ? void 0 : (_accountData_userProfile = accountData.userProfile) === null || _accountData_userProfile === void 0 ? void 0 : (_accountData_userProfile_user = _accountData_userProfile.user) === null || _accountData_userProfile_user === void 0 ? void 0 : _accountData_userProfile_user.userId;
    const { data } = useForReviewNotificationsQuery(ForReviewListStatus.All);
    useEffect(()=>{
        if (!accountId || !userId) {
            return;
        }
        const newSub = pushes(UserChannel(AccountId(accountId), UserId(userId)), EventType(Action.Create, ObjectType.NitroReview)).subscribe(()=>{
            void queryClient.invalidateQueries({
                queryKey: FOR_REVIEW_QUERY_KEYS.all
            });
        });
        const resolvedSub = pushes(UserChannel(AccountId(accountId), UserId(userId)), EventType(Action.Create, ObjectType.NitroReviewResolved)).subscribe(()=>{
            void queryClient.invalidateQueries({
                queryKey: FOR_REVIEW_QUERY_KEYS.all
            });
        });
        return ()=>{
            newSub.unsubscribe();
            resolvedSub.unsubscribe();
        };
    }, [
        accountId,
        userId,
        queryClient
    ]);
    return (_ref = data === null || data === void 0 ? void 0 : data.filter((group)=>{
        const latest = group.notifications[0];
        return latest != null && isForReviewUnread(latest);
    }).length) !== null && _ref !== void 0 ? _ref : 0;
};
